import type { BiomassDataRow } from '../../utils/productionUnitTable'
import type { EntityMutationResult } from '../../utils/entityMutationSync'
import type { FishStockLog, HarvestLog, MortalityLog, WeightSampleLog } from '../../types/production'
import { fishStockLogApi } from '../../utils/fishStockLogApi'
import { harvestLogApi } from '../../utils/harvestLogApi'
import { mortalityLogApi } from '../../utils/mortalityLogApi'
import { weightSampleLogApi } from '../../utils/weightSampleLogApi'
import { findAllLinkedFishStockLogsForRow } from './fishStockLogBridge'
import { findAllLinkedHarvestLogsForRow } from './harvestLogBridge'
import { findLinkedMortalityLog } from './mortalityLogBridge'
import { findAllLinkedWeightSampleLogsForRow } from './weightSampleLogBridge'

/**
 * I-2 delete-parity, single enumeration point
 * (see [docs/architecture-concerns.md](../../../docs/architecture-concerns.md) I-2).
 *
 * When a `ProductionRecord` (Tier B) row is deleted, every paired Tier A
 * event on its `(calendar date, lot)` must be deleted too. Leaving any behind
 * re-inflates the daily rollup / overlay on the next remote refresh — silent
 * cross-device data corruption (findings #65, #67, and the T3-D `HarvestLog`
 * gap that `Datatable.deleteRowById` shipped with because harvest landed
 * after those call sites were last audited).
 *
 * Both delete surfaces build their plan here so the two paths cannot drift:
 *   - Biomass recent-log lists — `Biomass.tsx` `deleteLogRow`
 *   - Database table — `Datatable.tsx` `deleteRowById`
 * Adding a future Tier A stream (e.g. `LotCostEvent`) is one edit in this file.
 *
 * Mortality is intentionally 1:1 — it matches the long-standing behavior of
 * both screens via `findLinkedMortalityLog` (metadata `productionRowId` with a
 * strict `(lot, day)` fallback). Weight / fish-stock / harvest delete EVERY
 * same-day event for the row because their rollups aggregate all of them.
 *
 * NOTE (open follow-up): a `(lot, day)` with multiple `MortalityLog` events
 * still orphans all but one on a row delete. Preserved here as the historical
 * contract; widening it to "delete all" is a separate decision because it
 * touches the MORT-12 rollup semantics and existing tests.
 */

type LinkableRow = Pick<BiomassDataRow, 'id' | 'date' | 'lot'>

export type ProductionRecordLinkedEventPlan = {
  mortalityLogIds: string[]
  weightSampleLogIds: string[]
  fishStockLogIds: string[]
  harvestLogIds: string[]
}

export type ProductionRecordLinkedEventDeleteInput = {
  targetRow: LinkableRow
  mortalityLogs: MortalityLog[]
  weightSampleLogs: WeightSampleLog[]
  fishStockLogs: FishStockLog[]
  harvestLogs: HarvestLog[]
}

/** Total number of Tier A events a plan will delete. */
export const countPlannedLinkedEventDeletes = (plan: ProductionRecordLinkedEventPlan): number =>
  plan.mortalityLogIds.length
  + plan.weightSampleLogIds.length
  + plan.fishStockLogIds.length
  + plan.harvestLogIds.length

/**
 * Pure: resolve which Tier A event ids are linked to a production row.
 * Runs at confirm-dialog open time (to show the count) and again right
 * before the delete executes.
 */
export const planProductionRecordLinkedEventDeletes = (
  input: ProductionRecordLinkedEventDeleteInput
): ProductionRecordLinkedEventPlan => {
  const { targetRow } = input
  const linkedMortalityLog = findLinkedMortalityLog(targetRow, input.mortalityLogs)
  return {
    mortalityLogIds: linkedMortalityLog ? [linkedMortalityLog.id] : [],
    weightSampleLogIds: findAllLinkedWeightSampleLogsForRow(targetRow, input.weightSampleLogs).map((log) => log.id),
    fishStockLogIds: findAllLinkedFishStockLogsForRow(targetRow, input.fishStockLogs).map((log) => log.id),
    harvestLogIds: findAllLinkedHarvestLogsForRow(targetRow, input.harvestLogs).map((log) => log.id)
  }
}

export type ProductionRecordLinkedEventDeleteResult = {
  plan: ProductionRecordLinkedEventPlan
  /** One entry per deleted event, in stream order, for `combineEntityMutationResults`. */
  mutationResults: Array<Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'>>
}

/**
 * Execute a plan: `DELETE` every linked Tier A event, awaited, in stream
 * order (mortality -> weight -> fish-stock -> harvest). Throws on the first
 * failed delete so the caller's catch can surface it; on the throw path no
 * result is returned, so callers reconcile their local state from `plan`
 * only after this resolves successfully.
 *
 * The `ProductionRecord` row itself is NOT deleted here — each screen keeps
 * its own row-removal path (`deleteProductionUnitTableRowAndSync` +
 * `setProductionRows` in Biomass; `dequeueProductionRow` + local-store
 * rewrite in Datatable) because their persistence models differ.
 */
export const executeProductionRecordLinkedEventDeletes = async (
  plan: ProductionRecordLinkedEventPlan,
  // Findings #66: mortality / weight-sample deletes are audited and carry the
  // reason the user typed when confirming the row delete.
  audit: { reason: string }
): Promise<ProductionRecordLinkedEventDeleteResult> => {
  const mutationResults: Array<Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'>> = []

  for (const id of plan.mortalityLogIds) {
    mutationResults.push(await mortalityLogApi.delete(id, audit))
  }
  for (const id of plan.weightSampleLogIds) {
    mutationResults.push(await weightSampleLogApi.delete(id, audit))
  }
  for (const id of plan.fishStockLogIds) {
    mutationResults.push(await fishStockLogApi.delete(id))
  }
  for (const id of plan.harvestLogIds) {
    mutationResults.push(await harvestLogApi.delete(id))
  }

  return { plan, mutationResults }
}
